import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StatCardProps {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  color?: 'default' | 'success' | 'warning' | 'error';
  className?: string;
  onClick?: () => void;
  'data-testid'?: string;
}

const colorMap = {
  default: 'text-foreground',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-destructive',
} as const;

const iconBgMap = {
  default: 'bg-muted text-muted-foreground',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  error: 'bg-destructive/10 text-destructive',
} as const;

export function StatCard({ label, value, icon: Icon, color = 'default', className, onClick, 'data-testid': testId }: StatCardProps) {
  const Comp = onClick ? 'button' : 'div';

  return (
    <Comp
      className={cn(
        'flex items-center gap-3 rounded-lg p-3 min-w-0 text-left',
        // Mobile: compact row
        'flex-row',
        // Desktop: vertical card with border
        'md:flex-col md:items-start md:border md:border-border',
        onClick && 'cursor-pointer hover:bg-accent transition-colors',
        className,
      )}
      onClick={onClick}
    >
      {Icon && (
        <div className={cn('hidden md:flex h-8 w-8 items-center justify-center rounded-md', iconBgMap[color])}>
          <Icon className="h-4 w-4" />
        </div>
      )}
      <div className="min-w-0">
        <p className={cn('text-lg font-bold tabular-nums md:text-xl', colorMap[color])} data-testid={testId}>
          {value}
        </p>
        <p className="text-xs text-muted-foreground truncate">{label}</p>
      </div>
    </Comp>
  );
}
